/**
 * O que acontece quando alguém confirma uma proposta do agente.
 *
 * Fica fora dos adaptadores porque a execução é a MESMA que a tela faria: criar
 * agendamento, criar tarefa, salvar lembrete. Um caminho paralelo para "o que a
 * IA pediu" acabaria com validação diferente da do caminho humano — e a
 * validação que só um dos dois tem é a que falta quando importa.
 */

import type { PortaDeDados } from './porta';
import type { PropostaDoAgente } from './tipos';

function texto(campos: Record<string, unknown>, chave: string): string {
    const valor = campos[chave];
    return typeof valor === 'string' ? valor.trim() : '';
}

export async function executarProposta(porta: PortaDeDados, proposta: PropostaDoAgente): Promise<{ descricao: string }> {
    const c = proposta.campos ?? {};

    if (proposta.ferramenta === 'agenda_agendar') {
        const paciente = texto(c, 'paciente');
        const data = texto(c, 'data');
        const hora = texto(c, 'hora');
        const procedimento = texto(c, 'procedimento');
        if (!paciente || !data || !hora || !procedimento) {
            throw new Error('A proposta está incompleta: paciente, data, hora e procedimento são obrigatórios.');
        }
        const criado = await porta.criarAgendamento({
            pacienteNome: paciente,
            data,
            hora,
            duracaoMin: Number(c.duracao_min ?? 60),
            procedimento,
            profissional: texto(c, 'profissional'),
            teleconsulta: false,
            lembretePorWhatsapp: true,
            repetirSemanalmente: false,
        });
        return { descricao: `${criado.paciente} · ${criado.procedimento} em ${data} às ${hora}` };
    }

    if (proposta.ferramenta === 'followup_criar') {
        const titulo = texto(c, 'titulo');
        if (!titulo) throw new Error('A proposta está incompleta: o follow-up precisa de um título.');
        const paciente = texto(c, 'paciente');
        const tarefa = await porta.criarTarefa({
            /* O nome do paciente entra no título porque é assim que a recepção
               encontra a tarefa depois — e porque a tarefa não tem campo de
               vínculo na tela de hoje. */
            titulo: paciente ? `${titulo} — ${paciente}` : titulo,
            ...(texto(c, 'vence_em') ? { venceEm: texto(c, 'vence_em') } : {}),
        });
        return { descricao: `${tarefa.titulo} · vence em ${tarefa.venceEm}` };
    }

    if (proposta.ferramenta === 'regua_criar') {
        const quando = texto(c, 'quando');
        const descricao = texto(c, 'descricao');
        const offset = Number(c.offset_min ?? NaN);
        if (!quando || !descricao || !Number.isFinite(offset)) {
            throw new Error('A proposta está incompleta: quando, antecedência e texto são obrigatórios.');
        }
        const canal = texto(c, 'canal');
        const lembrete = await porta.salvarLembrete(null, {
            quando,
            descricao,
            offsetMin: Math.max(0, offset),
            canal: canal === 'email' || canal === 'ambos' ? canal : 'whatsapp',
            ativo: true,
        });
        return { descricao: `${lembrete.quando} · ${lembrete.descricao}` };
    }

    throw new Error(`A ferramenta ${proposta.ferramenta} não sabe ser confirmada por esta tela.`);
}
